import { useEffect, useState } from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import CircularProgress from '@material-ui/core/CircularProgress';

type GcpProject = {
  projectId: string;
  displayName: string;
};

type ProjectsResponse = {
  projects?: GcpProject[];
};

export const GcpProjectPicker = (
  props: FieldExtensionComponentProps<string>,
): JSX.Element => {
  const { onChange, formData, schema, required, rawErrors } = props;
  const discovery = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const [projects, setProjects] = useState<GcpProject[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadProjects = async () => {
      setLoading(true);
      try {
        const base = await discovery.getBaseUrl('gcp-projects');
        const res = await fetchApi.fetch(`${base}/projects`);
        const data = (await res.json()) as ProjectsResponse;
        if (!cancelled) {
          setProjects(data.projects ?? []);
        }
      } catch {
        if (!cancelled) {
          setProjects([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadProjects();

    return () => {
      cancelled = true;
    };
  }, [discovery, fetchApi]);

  const hasError = Boolean(rawErrors?.length) && !formData;

  return (
    <Autocomplete<GcpProject, false, false, true>
      freeSolo
      autoSelect
      loading={loading}
      options={projects}
      // Campo é uma string (o projectId). Controlamos pelo inputValue para evitar
      // que o label do objeto seja regravado no valor (causava recursão).
      inputValue={formData ?? ''}
      onInputChange={(_event, value) => onChange(value || undefined)}
      // label == valor (projectId) para não transformar o texto digitado/selecionado.
      getOptionLabel={option =>
        typeof option === 'string' ? option : option.projectId
      }
      // No dropdown mostramos o nome amigável + id.
      renderOption={option => `${option.displayName} (${option.projectId})`}
      // Filtra por id OU nome (o getOptionLabel só expõe o id).
      filterOptions={(opts, state) => {
        const q = state.inputValue.trim().toLowerCase();
        if (!q) {
          return opts;
        }
        return opts.filter(
          o =>
            o.projectId.toLowerCase().includes(q) ||
            o.displayName.toLowerCase().includes(q),
        );
      }}
      renderInput={params => (
        <TextField
          {...params}
          label={schema.title}
          helperText={schema.description}
          required={required}
          error={hasError}
          variant="outlined"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};
